// src/components/ModalCarteraInicial.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import "animate.css";
import { postCarteraInicial } from "../helpers/rutaCarteras";

const toISOorNull = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return null;
  const d = new Date(yyyy_mm_dd + "T00:00:00");
  return isNaN(d.getTime()) ? null : d.toISOString();
};

export default function ModalCarteraInicial({ show, onClose, onSaved }) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  const [form, setForm] = useState({
    // base
    tipo: "CHEQUE",
    estado: "EN_CARTERA",
    importe: "",
    recibidoEn: today,

    // ✅ 8 campos unificados (para CHEQUE e ICHEQ)
    fechaCheque: today,     // FECHA CHEQUE
    bancoEmisor: "",        // BANCO EMISOR
    pagadoA: "",            // PAGADO A (beneficiario)
    cuitPagadoA: "",
    cobradoA: "",           // COBRADO A (librador/emisor)
    cuitCobradoA: "",
    fechaPago: "",          // FECHA PAGO / LIQUIDACIÓN
    observaciones: "",

    // identificadores por tipo (se usan para armar nro)
    nroCheque: "",
    idICheq: "",
  });

  useEffect(() => {
    if (show) {
      setErr("");
      setSaving(false);
      setShowSuccess(false);
      setForm((f) => ({
        ...f,
        recibidoEn: today,
        fechaCheque: f.fechaCheque || today,
        estado: f.estado || "EN_CARTERA",
      }));
    }
  }, [show, today]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const identValue = form.tipo === "CHEQUE" ? form.nroCheque : form.idICheq;
  const setIdentValue = (v) =>
    form.tipo === "CHEQUE" ? set("nroCheque", v) : set("idICheq", v);

  const validate = () => {
    const imp = Number(form.importe);
    if (!form.tipo) return "Falta Tipo.";
    if (!imp || imp <= 0) return "Importe debe ser mayor a 0.";
    if (!form.recibidoEn) return "Falta fecha 'Recibido en'.";
    if (!form.fechaCheque) return "Falta 'Fecha Cheque'.";
    if (!identValue?.trim()) {
      return form.tipo === "CHEQUE" ? "Falta Nro Cheque." : "Falta ID iCheq.";
    }
    return "";
  };

  const handleSave = async () => {
    const v = validate();
    if (v) return setErr(v);

    setErr("");
    setSaving(true);

    try {
      const tipo = form.tipo;
      const identificador = (identValue || "").trim();

      const payload = {
        // base
        tipo,
        estado: form.estado,
        importe: Number(form.importe) || 0,
        recibidoEn: toISOorNull(form.recibidoEn) || new Date().toISOString(),

        // ✅ NUEVOS CAMPOS UNIFICADOS (los que querés en UI/PDF)
        fechaCheque: toISOorNull(form.fechaCheque) || undefined,
        nro: identificador || undefined,
        bancoEmisor: form.bancoEmisor?.trim() || undefined,

        pagadoA: form.pagadoA?.trim() || undefined,
        cuitPagadoA: form.cuitPagadoA?.trim() || undefined,

        cobradoA: form.cobradoA?.trim() || undefined,
        cuitCobradoA: form.cuitCobradoA?.trim() || undefined,

        fechaPago: toISOorNull(form.fechaPago) || undefined,
        observaciones: form.observaciones?.trim() || undefined,

        // ✅ LEGACY (compatibilidad con datos viejos / pantallas viejas)
        ...(tipo === "CHEQUE"
          ? {
              nroCheque: identificador || undefined,
              librador: form.cobradoA?.trim() || undefined,
              cuitLibrador: form.cuitCobradoA?.trim() || undefined,
              fechaPagoDiferido: toISOorNull(form.fechaPago) || undefined,
            }
          : {
              idICheq: identificador || undefined,
              emisor: form.cobradoA?.trim() || undefined,
              fechaLiquidacion: toISOorNull(form.fechaPago) || undefined,
            }),
      };

      await postCarteraInicial(payload);

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onClose?.();
        onSaved?.();
      }, 900);
    } catch (e) {
      setErr(e?.response?.data?.msg || e?.message || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        centered
        backdrop="static"
        keyboard={!saving}
        dialogClassName="animate__animated animate__fadeInDown"
        size="lg"
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title style={{ fontSize: 20, fontWeight: 700 }}>
            Agregar saldo inicial (Cartera)
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ fontSize: 15 }}>
          {err ? (
            <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 14 }}>
              {err}
            </div>
          ) : null}

          <div className="container p-0">
            {/* Cabecera principal */}
            <div className="form-row row">
              <div className="form-group mt-3 col-sm-3">
                <label className="fw-semibold">Tipo</label>
                <select
                  className="form-control"
                  value={form.tipo}
                  onChange={(e) => set("tipo", e.target.value)}
                  disabled={saving}
                >
                  <option value="CHEQUE">CHEQUE</option>
                  <option value="ICHEQ">ICHEQ</option>
                </select>
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label className="fw-semibold">Estado</label>
                <select
                  className="form-control"
                  value={form.estado}
                  onChange={(e) => set("estado", e.target.value)}
                  disabled={saving}
                >
                  <option value="EN_CARTERA">EN_CARTERA</option>
                  <option value="DEPOSITADO">DEPOSITADO</option>
                  <option value="ACREDITADO">ACREDITADO</option>
                  <option value="RECHAZADO">RECHAZADO</option>
                  <option value="ANULADO">ANULADO</option>
                  <option value="EMITIDO">EMITIDO</option>
                </select>
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label className="fw-semibold">Recibido en</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.recibidoEn}
                  onChange={(e) => set("recibidoEn", e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label className="fw-semibold">Importe</label>
                <input
                  className="form-control"
                  value={form.importe}
                  onChange={(e) => set("importe", e.target.value)}
                  placeholder="0"
                  inputMode="decimal"
                  disabled={saving}
                />
              </div>
            </div>

            {/* ✅ fila 1 común */}
            <div className="form-row row">
              <div className="form-group mt-3 col-sm-3">
                <label className="fw-semibold">Fecha cheque *</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.fechaCheque}
                  onChange={(e) => set("fechaCheque", e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="form-group mt-3 col-sm-5">
                <label className="fw-semibold">Pagado a (Beneficiario)</label>
                <input
                  className="form-control"
                  value={form.pagadoA}
                  onChange={(e) => set("pagadoA", e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label className="fw-semibold">CUIT Beneficiario</label>
                <input
                  className="form-control"
                  value={form.cuitPagadoA}
                  onChange={(e) => set("cuitPagadoA", e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>

            {/* ✅ fila 2 común */}
            <div className="form-row row">
              <div className="form-group mt-3 col-sm-3">
                <label className="fw-semibold">Banco emisor</label>
                <input
                  className="form-control"
                  value={form.bancoEmisor}
                  onChange={(e) => set("bancoEmisor", e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="form-group mt-3 col-sm-5">
                <label className="fw-semibold">
                  Cobrado a ({form.tipo === "CHEQUE" ? "Librador" : "Emisor"})
                </label>
                <input
                  className="form-control"
                  value={form.cobradoA}
                  onChange={(e) => set("cobradoA", e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label className="fw-semibold">
                  CUIT {form.tipo === "CHEQUE" ? "Librador" : "Emisor"}
                </label>
                <input
                  className="form-control"
                  value={form.cuitCobradoA}
                  onChange={(e) => set("cuitCobradoA", e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>

            <hr className="my-4" />

            {/* ✅ dinámica mínima (solo el identificador) */}
            <div className="mb-2" style={{ fontWeight: 700, fontSize: 16 }}>
              Datos del {form.tipo === "CHEQUE" ? "Cheque" : "iCheq"}
            </div>

            <div className="form-row row">
              <div className="form-group mt-3 col-sm-4">
                <label className="fw-semibold">
                  {form.tipo === "CHEQUE" ? "Nro Cheque *" : "ID iCheq *"}
                </label>
                <input
                  className="form-control"
                  value={identValue}
                  onChange={(e) => setIdentValue(e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label className="fw-semibold">Fecha pago / liquidación</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.fechaPago}
                  onChange={(e) => set("fechaPago", e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label className="fw-semibold">Observaciones</label>
                <input
                  className="form-control"
                  value={form.observaciones}
                  onChange={(e) => set("observaciones", e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="mt-4 text-muted" style={{ fontSize: 13 }}>
              * Esto entra como <b>saldo inicial</b>. No toca caja/banco hasta que lo deposites/acredites.
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>

          <button className="btn btn-dark" onClick={handleSave} disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>🧾</div>
          <h5 className="mt-2">¡Cheque/iCheq cargado correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
}
